import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function LoginPage() {
  const { user, loading, login, loginWithMicrosoft } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  if (loading) {
    return (
      <div className="app-shell flex items-center justify-center bg-bg-primary">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  async function handleSignIn(provider: 'google' | 'microsoft') {
    setError(null);
    setSigningIn(true);
    try {
      if (provider === 'microsoft') {
        await loginWithMicrosoft();
      } else {
        await login();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      if (!message.includes('popup-closed-by-user')) {
        setError(message);
      }
    } finally {
      setSigningIn(false);
    }
  }

  return (
    <div className="app-shell flex items-center justify-center bg-bg-primary">
      <div className="w-full max-w-sm p-8 bg-bg-card rounded-xl border border-border">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary tracking-tight">
            Dossier
          </h1>
          <p className="text-text-muted text-sm mt-2">
            Meeting intelligence, organized.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-danger/10 border border-danger/20 text-danger text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => handleSignIn('google')}
            disabled={signingIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-bg-input border border-border hover:border-border-hover text-text-primary font-medium transition-colors disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {signingIn ? 'Signing in...' : 'Sign in with Google'}
          </button>

          <button
            onClick={() => handleSignIn('microsoft')}
            disabled={signingIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-bg-input border border-border hover:border-border-hover text-text-primary font-medium transition-colors disabled:opacity-50"
          >
            <svg viewBox="0 0 21 21" className="w-5 h-5">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            {signingIn ? 'Signing in...' : 'Sign in with Microsoft'}
          </button>
        </div>
      </div>
    </div>
  );
}
